// frontend/src/utils/conversation/buildIntentState.js

function normalizeText(value = '') {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ');
}

function getStateText(value = null) {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object') {
    return String(
      value.text ||
      value.statement ||
      value.label ||
      value.type ||
      ''
    ).trim();
  }
  return '';
}

function normalizeActiveUserIntent(value = null) {
  if (!value) return null;

  if (typeof value === 'string') {
    return {
      type: 'conversation_objective',
      text: value.trim(),
      confidence: 0.65,
      status: 'ACTIVE',
      source: 'legacy_string',
    };
  }

  if (typeof value !== 'object') return null;

  const text = getStateText(value);
  if (!text) return null;

  return {
    ...value,
    type: value.type || 'conversation_objective',
    text,
    confidence:
      typeof value.confidence === 'number'
        ? value.confidence
        : 0.65,
    status: value.status || 'ACTIVE',
    source: value.source || 'existing_state',
  };
}

function buildIntentFromObjective(objectiveDecisionState = null) {
  const objective = objectiveDecisionState?.activeObjective || null;
  const decision = objectiveDecisionState?.activeDecision || null;
  const objectiveText = getStateText(objective);
  const decisionText = getStateText(decision);

  if (!objectiveText && !decisionText) return null;

  return {
    type: 'conversation_objective',
    text: objectiveText || decisionText,
    decision: decisionText || null,
    confidence: Math.max(
      objective?.confidence || 0,
      decision?.confidence || 0,
      0.7
    ),
    status: 'ACTIVE',
    source:
      objective?.source ||
      decision?.source ||
      'objective_decision_state',
  };
}

function mapTurnFunction({
  message = '',
  turnInterpretation = null,
  objectiveDecisionState = null,
  turnSignalArbitration = null,
} = {}) {
  const normalized = normalizeText(message);
  const turnAct = turnInterpretation?.continuity?.turnAct || 'unknown';
  const dominantSignal =
    turnSignalArbitration?.dominantSignal ||
    turnInterpretation?.continuity?.dominantSignal ||
    'none';

  if (objectiveDecisionState?.signals?.objectiveExited === true) {
    return {
      type: 'EXIT',
      confidence: 0.99,
      source: 'objective_exit',
    };
  }

  if (dominantSignal === 'reasoningContinuation') {
    const counterEvidenceLike = /^(but|however|although|except|yet)\b/.test(normalized);
    return {
      type: counterEvidenceLike ? 'COUNTER_EVIDENCE' : 'EVIDENCE',
      confidence:
        turnSignalArbitration?.dominantSignalScore || 0.9,
      source: 'turn_signal_arbitration',
    };
  }

  if (dominantSignal === 'decisionSupport') {
    return {
      type: 'DECISION_REQUEST',
      confidence:
        turnSignalArbitration?.dominantSignalScore || 0.85,
      source: 'turn_signal_arbitration',
    };
  }

  if (dominantSignal === 'objectiveContinuation') {
    return {
      type: turnAct === 'new_question' ? 'QUESTION' : 'CONTEXT',
      confidence:
        turnSignalArbitration?.dominantSignalScore || 0.65,
      source: 'turn_signal_arbitration',
    };
  }

  const turnActMap = {
    answer: 'ANSWER',
    agreement: 'AGREEMENT',
    clarification: 'CLARIFICATION',
    correction: 'CORRECTION',
    expansion: 'EXPANSION',
    followup_question: 'QUESTION',
    new_question: 'QUESTION',
    new_statement: 'OBSERVATION',
  };

  return {
    type: turnActMap[turnAct] || 'OBSERVATION',
    confidence:
      turnInterpretation?.continuity?.attachmentConfidence || 0.5,
    source: 'turn_attachment',
  };
}

function mapResponseIntent(turnSignalArbitration = null) {
  const dominantSignal = turnSignalArbitration?.dominantSignal || 'none';
  const score = turnSignalArbitration?.dominantSignalScore || 0;

  const mappings = {
    decisionSupport: 'SUPPORT_DECISION',
    reasoningContinuation: 'INTEGRATE_EVIDENCE',
    objectiveContinuation: 'CONTINUE_ACTIVE_INTENT',
    topicContinuation: 'CONTINUE_TOPIC',
    contextualContinuation: 'CONTINUE_CONTEXT',
    targetShift: 'ANSWER_NEW_TARGET',
    closure: 'CLOSE_OR_REDIRECT',
    pendingAnswer: 'HANDLE_PENDING_ANSWER',
    emotionalDisclosure: 'RESPOND_WITH_EMOTIONAL_PRESENCE',
  };

  return {
    type: mappings[dominantSignal] || 'ANSWER_NORMALLY',
    confidence: score,
    source: 'turn_signal_arbitration',
  };
}

function isWeakCandidate(candidate = null) {
  const intent = candidate?.intent || 'unknown';
  const confidence = candidate?.confidence || 0;

  return (
    ['unknown', 'null'].includes(intent) ||
    confidence < 0.5
  );
}

function isExplicitReplacementCandidate({
  message = '',
  candidate = null,
  objectiveDecisionState = null,
} = {}) {
  const normalized = normalizeText(message);
  const explicitShift =
    objectiveDecisionState?.signals?.objectiveExited === true ||
    /\b(different question|new question|new topic|switch topics?|change topics?)\b/.test(normalized);

  return explicitShift && !isWeakCandidate(candidate);
}

function buildIntentState({
  message = '',
  currentIntentState = null,
  turnInterpretation = null,
  objectiveDecisionState = null,
  turnSignalArbitration = null,
  shouldResetEphemeral = false,
} = {}) {
  const turnIntentCandidate =
    turnInterpretation?.turnIntentCandidate || null;
  const previousActiveUserIntent = normalizeActiveUserIntent(
    currentIntentState?.activeUserIntent || null
  );
  const objectiveIntent = buildIntentFromObjective(
    objectiveDecisionState
  );
  const objectiveExited =
    objectiveDecisionState?.signals?.objectiveExited === true;

  const turnFunction = mapTurnFunction({
    message,
    turnInterpretation,
    objectiveDecisionState,
    turnSignalArbitration,
  });

  const responseIntent = mapResponseIntent(
    turnSignalArbitration
  );

  let activeUserIntent = previousActiveUserIntent;
  let transition = 'CONTINUE';
  let reason = 'Preserved active user intent.';

  if (shouldResetEphemeral) {
    activeUserIntent = null;
    transition = 'EXIT';
    reason = 'Fresh open reset ephemeral user intent.';
  } else if (objectiveExited) {
    if (
      isExplicitReplacementCandidate({
        message,
        candidate: turnIntentCandidate,
        objectiveDecisionState,
      })
    ) {
      activeUserIntent = {
        type: turnIntentCandidate.intent,
        text: String(message || '').trim(),
        confidence: turnIntentCandidate.confidence || 0.65,
        status: 'ACTIVE',
        source: turnIntentCandidate.source || 'turn_intent_candidate',
      };
      transition = 'REPLACE';
      reason = 'User explicitly exited the prior intent and introduced a new one.';
    } else {
      activeUserIntent = null;
      transition = 'EXIT';
      reason = 'User explicitly exited the prior intent.';
    }
  } else if (objectiveIntent) {
    activeUserIntent = objectiveIntent;
    transition = previousActiveUserIntent ? 'REFINE' : 'CONTINUE';
    reason = previousActiveUserIntent
      ? 'Objective state refined the active user intent.'
      : 'Objective state established the active user intent.';
  } else if (!previousActiveUserIntent && !isWeakCandidate(turnIntentCandidate)) {
    activeUserIntent = {
      type: turnIntentCandidate.intent,
      text: String(message || '').trim(),
      confidence: turnIntentCandidate.confidence || 0.65,
      status: 'ACTIVE',
      source: turnIntentCandidate.source || 'turn_intent_candidate',
    };
    transition = 'CONTINUE';
    reason = 'Strong turn candidate established the active user intent.';
  } else if (previousActiveUserIntent && isWeakCandidate(turnIntentCandidate)) {
    activeUserIntent = previousActiveUserIntent;
    transition = 'CONTINUE';
    reason = 'Weak or null turn candidate did not replace the active user intent.';
  }

  return {
    activeUserIntent,
    turnIntentCandidate,
    turnFunction,
    responseIntent,
    transition,
    confidence: Math.max(
      activeUserIntent?.confidence || 0,
      turnFunction?.confidence || 0,
      responseIntent?.confidence || 0
    ),
    reason,
  };
}

export default buildIntentState;
